import { useState } from 'react';
import { money } from '../format';
import type { CustomerFlagIn, Identity, KeyOut, KeyUpdateIn } from '../types';

interface KeyActionsProps {
  keyRow: KeyOut;
  identity: Identity | null;
  onUpdate: (id: string, data: KeyUpdateIn) => Promise<void>;
  onDisable: (id: string) => Promise<void>;
  onSetCustomer?: ((id: string, data: CustomerFlagIn) => Promise<void>) | undefined;
  onRequestIncrease?: ((key: KeyOut) => void) | undefined;
  onRequestGuardrail?: ((key: KeyOut) => void) | undefined;
  onSetupClaudeCode?: ((key: KeyOut) => void) | undefined;
  onChangeGuardrail?: ((key: KeyOut) => void) | undefined;
}

/**
 * Row actions for managed keys: rename, disable, decrease limit, request increase. Only
 * shown when the viewer has permission based on Identity (team membership, platform admin).
 * Server is authoritative — this is UI courtesy to avoid offering actions that will 403.
 */
export function KeyActions({ keyRow, identity, onUpdate, onDisable, onSetCustomer, onRequestIncrease, onRequestGuardrail, onSetupClaudeCode, onChangeGuardrail }: KeyActionsProps) {
  const [mode, setMode] = useState<'idle' | 'rename' | 'limit' | 'customer' | 'disabling'>('idle');
  const [label, setLabel] = useState(keyRow.label ?? '');
  const [limitUsd, setLimitUsd] = useState(
    keyRow.limit_usd !== null ? String(keyRow.limit_usd) : '',
  );
  const [isCustomerKey, setIsCustomerKey] = useState(keyRow.is_customer_key);
  const [customerId, setCustomerId] = useState(keyRow.customer_id ?? '');
  const [unlimited, setUnlimited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show actions for managed keys
  if (!keyRow.is_managed) {
    return null;
  }

  // Check if the viewer can act on this key based on Identity
  const canAct = canActOnKey(keyRow, identity);
  if (!canAct) {
    return null;
  }

  const handleRename = async () => {
    if (!label.trim()) {
      setError('Label cannot be empty');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onUpdate(keyRow.id, { label: label.trim() });
      setMode('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // A platform admin may set any limit; everyone else may only lower one (the server
  // enforces both, and the team budget cap on an admin's raise).
  const isAdmin = identity?.is_platform_admin === true;

  const handleLimitChange = async () => {
    if (isAdmin && unlimited) {
      await submitLimit(null);
      return;
    }
    const newLimit = parseFloat(limitUsd);
    if (isNaN(newLimit) || newLimit < 0) {
      setError('Limit must be a non-negative number');
      return;
    }

    // Client-side hint: limits can only decrease — increases go through the request flow
    if (!isAdmin && keyRow.limit_usd !== null && newLimit > keyRow.limit_usd) {
      setError(
        'Limits can only be decreased. Use "Request increase" to request a temporary increase.',
      );
      return;
    }

    // Warn if lowering below current usage
    const currentUsage = getCurrentPeriodUsage(keyRow);
    if (currentUsage !== null && newLimit < currentUsage) {
      if (
        !confirm(
          `Warning: The new limit ($${newLimit.toFixed(2)}) is below current usage (${money(currentUsage)}). ` +
            `The key will start failing with HTTP 402 immediately. Continue?`,
        )
      ) {
        return;
      }
    }

    await submitLimit(newLimit);
  };

  const submitLimit = async (newLimit: number | null) => {
    setSubmitting(true);
    setError(null);
    try {
      await onUpdate(keyRow.id, { limit_usd: newLimit });
      setMode('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (
      !confirm(
        `Disable ${keyRow.name}?\n\n` +
          `This will revoke the key — it will stop working while its spend history is kept. ` +
          `This cannot be undone through this UI.`,
      )
    ) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setMode('disabling');
    try {
      await onDisable(keyRow.id);
      setMode('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setMode('idle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetCustomer = async () => {
    // Validate: if marking as customer key, customer_id must be non-empty
    if (isCustomerKey && !customerId.trim()) {
      setError('Customer ID is required when marking a key as a customer key');
      return;
    }

    const data: CustomerFlagIn = {
      is_customer_key: isCustomerKey,
      customer_id: isCustomerKey ? customerId.trim() : null,
    };

    setSubmitting(true);
    setError(null);
    try {
      if (onSetCustomer) {
        await onSetCustomer(keyRow.id, data);
      }
      setMode('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'rename') {
    return (
      <div className="key-action-form">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={submitting}
          placeholder="New label"
        />
        <button className="primary" onClick={handleRename} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => setMode('idle')} disabled={submitting}>
          Cancel
        </button>
        {error ? <div className="action-error">{error}</div> : null}
      </div>
    );
  }

  if (mode === 'limit') {
    return (
      <div className="key-action-form">
        <input
          type="number"
          value={limitUsd}
          onChange={(e) => setLimitUsd(e.target.value)}
          disabled={submitting || (isAdmin && unlimited)}
          placeholder="New limit"
          aria-label="New limit"
          step="0.01"
          min="0"
        />
        {isAdmin ? (
          <label className="inline-check small">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              disabled={submitting}
            />{' '}
            No limit
          </label>
        ) : null}
        <button className="primary" onClick={handleLimitChange} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => setMode('idle')} disabled={submitting}>
          Cancel
        </button>
        {error ? <div className="action-error">{error}</div> : null}
        {isAdmin ? (
          <p className="action-note">As a platform admin you can raise or lower this limit. A raise must fit the team budget.</p>
        ) : (
          <p className="action-note warn">Limits can only be decreased. Use "Request increase" for temporary increases.</p>
        )}
      </div>
    );
  }

  if (mode === 'customer') {
    return (
      <div className="key-action-form">
        <label>
          <input
            type="checkbox"
            checked={isCustomerKey}
            onChange={(e) => setIsCustomerKey(e.target.checked)}
            disabled={submitting}
          />
          <span>Customer key</span>
        </label>
        {isCustomerKey ? (
          <input
            type="text"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            disabled={submitting}
            placeholder="Customer ID"
          />
        ) : null}
        <button className="primary" onClick={handleSetCustomer} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => setMode('idle')} disabled={submitting}>
          Cancel
        </button>
        {error ? <div className="action-error">{error}</div> : null}
      </div>
    );
  }

  // Show "Request increase" for keys with a numeric limit (not unlimited)
  const canRequestIncrease = keyRow.limit_usd !== null && onRequestIncrease;
  const canRequestGuardrail = onRequestGuardrail;
  const canManageCustomer = canSetCustomerFlag(keyRow, identity) && onSetCustomer;

  return (
    <div className="key-actions">
      {onSetupClaudeCode && !keyRow.disabled ? (
        <button onClick={() => onSetupClaudeCode(keyRow)} disabled={mode === 'disabling'}>
          Set up Claude Code
        </button>
      ) : null}
      <button onClick={() => setMode('rename')} disabled={mode === 'disabling'}>
        Rename
      </button>
      {keyRow.limit_usd !== null || isAdmin ? (
        <button onClick={() => setMode('limit')} disabled={mode === 'disabling'}>
          Change limit
        </button>
      ) : null}
      {canRequestIncrease ? (
        <button onClick={() => onRequestIncrease(keyRow)} disabled={mode === 'disabling'}>
          Request increase
        </button>
      ) : null}
      {isAdmin && onChangeGuardrail ? (
        <button onClick={() => onChangeGuardrail(keyRow)} disabled={mode === 'disabling'}>
          Change guardrail
        </button>
      ) : null}
      {canRequestGuardrail ? (
        <button onClick={() => onRequestGuardrail(keyRow)} disabled={mode === 'disabling'}>
          Request guardrail
        </button>
      ) : null}
      {canManageCustomer ? (
        <>
          <div className="customer-status small muted">
            {keyRow.is_customer_key ? (
              <>Customer: {keyRow.customer_id}</>
            ) : (
              <>Not a customer key</>
            )}
          </div>
          <button onClick={() => setMode('customer')} disabled={mode === 'disabling'}>
            Edit customer
          </button>
        </>
      ) : null}
      <button className="danger" onClick={handleDisable} disabled={mode === 'disabling'}>
        {mode === 'disabling' ? 'Disabling...' : 'Disable'}
      </button>
      {error ? <div className="action-error">{error}</div> : null}
    </div>
  );
}

/**
 * Check if the viewer can act on this key. Platform admins can act on any key. Team leads
 * can act on keys in teams they lead. Members can only act on their own keys.
 */
export function canActOnKey(key: KeyOut, identity: Identity | null): boolean {
  if (!identity) return false;
  if (identity.is_platform_admin) return true;

  // Check if the viewer is a lead or member of the key's team
  if (key.team_id) {
    const teamRole = identity.teams.find((t) => t.team_id === key.team_id);
    if (teamRole) {
      if (teamRole.role === 'lead') return true;
      // Members can only act on their own keys
      if (teamRole.role === 'member' && key.owner_email === identity.email) return true;
    }
  }

  return false;
}

/**
 * Check if the viewer can set the customer flag on this key. Only platform admins and
 * team leads can set the customer flag — plain members cannot, even if they own the key.
 */
export function canSetCustomerFlag(key: KeyOut, identity: Identity | null): boolean {
  if (!identity) return false;
  if (identity.is_platform_admin) return true;

  // Check if the viewer is a lead of the key's team (members are excluded)
  if (key.team_id) {
    const teamRole = identity.teams.find((t) => t.team_id === key.team_id);
    if (teamRole?.role === 'lead') return true;
  }

  return false;
}

/**
 * Get the current period usage based on the limit reset frequency.
 */
function getCurrentPeriodUsage(key: KeyOut): number | null {
  if (key.limit_reset === 'daily') return key.usage_daily;
  if (key.limit_reset === 'weekly') return key.usage_weekly;
  if (key.limit_reset === 'monthly') return key.usage_monthly;
  return key.usage; // lifetime
}
